import { useState } from 'react';

import type { CreateApiKeyResponse } from '../../hooks/use-api-keys';

interface NewKeyRevealProps {
  result: CreateApiKeyResponse;
  /** AGENT_MCP_PUBLIC_URL — set deployment-side, fetched from /config at boot. */
  mcpPublicUrl: string;
  /** AGENT_SERVER_PUBLIC_URL — same. */
  apiBaseUrl: string;
  onClose: () => void;
}

function CopyButton({ text, label = 'Copy', testId }: { text: string; label?: string; testId?: string }) {
  const [done, setDone] = useState(false);
  return (
    <button
      data-testid={testId}
      onClick={() => {
        navigator.clipboard.writeText(text).then(
          () => {
            setDone(true);
            setTimeout(() => setDone(false), 1500);
          },
          () => {
            /* clipboard rejected — surface as no-op for now */
          },
        );
      }}
      style={{
        padding: '4px 10px',
        fontSize: 11,
        background: 'transparent',
        border: '1px solid var(--border)',
        borderRadius: 4,
        cursor: 'pointer',
        color: done ? '#2f855a' : 'var(--text-soft)',
      }}
    >
      {done ? '✓ Copied' : label}
    </button>
  );
}

function CodeBlock({ children, testId }: { children: string; testId?: string }) {
  return (
    <div style={{ position: 'relative' }}>
      <pre
        data-testid={testId}
        style={{
          background: 'var(--bg-alt2)',
          border: '1px solid var(--border)',
          borderRadius: 4,
          padding: 12,
          fontSize: 12,
          fontFamily: 'ui-monospace, monospace',
          overflowX: 'auto',
          margin: 0,
          color: 'var(--text)',
        }}
      >
        {children}
      </pre>
      <div style={{ position: 'absolute', top: 6, right: 6 }}>
        <CopyButton text={children} />
      </div>
    </div>
  );
}

export function NewKeyReveal({ result, mcpPublicUrl, apiBaseUrl, onClose }: NewKeyRevealProps) {
  const isMcp = result.audience === 'mcp';
  const isOai = result.audience === 'openai_completions';

  // Claude Desktop config snippet — uses mcp-remote stdio bridge over the public MCP URL.
  // Real Claude Desktop config goes at:
  //   ~/Library/Application Support/Claude/claude_desktop_config.json (macOS)
  const claudeDesktopSnippet = JSON.stringify(
    {
      mcpServers: {
        shiftagent: {
          command: 'npx',
          args: ['-y', 'mcp-remote', mcpPublicUrl || 'https://mcp.shiftagent.io'],
          env: {
            MCP_REMOTE_AUTH: `Bearer ${result.secret}`,
          },
        },
      },
    },
    null,
    2,
  );

  const curlSnippet = `curl ${apiBaseUrl || 'https://api.shiftagent.io'}/profiles \\
  -H "Authorization: Bearer ${result.secret}"`;

  // OpenAI-compatible /chat/completions — the surface VAPI's custom-LLM block calls.
  const oaiBase = apiBaseUrl || 'https://api.shiftagent.io';
  const oaiCurlSnippet = `curl -N ${oaiBase}/chat/completions \\
  -H "Authorization: Bearer ${result.secret}" \\
  -H "Content-Type: application/json" \\
  -d '{"model":"custom","stream":true,"messages":[{"role":"user","content":"Hi"}]}'`;
  // VAPI custom-LLM provider config (paste into the assistant's model block).
  const vapiSnippet = JSON.stringify(
    {
      model: {
        provider: 'custom-llm',
        url: `${oaiBase}/chat/completions`,
        model: 'custom',
        headers: { Authorization: `Bearer ${result.secret}` },
      },
    },
    null,
    2,
  );

  return (
    <div
      data-testid="new-key-reveal"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
      }}
    >
      <div
        role="dialog"
        style={{
          width: 640,
          maxWidth: '92vw',
          maxHeight: '90vh',
          overflow: 'auto',
          background: 'var(--bg)',
          borderRadius: 8,
          padding: '20px 24px',
          border: '1px solid var(--border)',
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
          {result.custom_auth ? 'Key minted' : 'Key minted — copy it now'}
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-soft)', marginBottom: 16 }}>
          {result.custom_auth
            ? 'This key authenticates by the custom token you provided — we store only a hash and never display it. Use the same token you pasted.'
            : 'This is the only time the secret will be shown. After you close this dialog the platform stores only a hash.'}
        </div>

        {result.custom_auth ? (
          <div
            data-testid="new-key-custom-auth"
            style={{
              padding: 12,
              background: 'var(--bg-alt2)',
              border: '1px solid var(--border)',
              borderRadius: 4,
              fontSize: 13,
              color: 'var(--text-soft)',
              marginBottom: 20,
            }}
          >
            🔐 Authenticates by your custom token (not shown). Configure VAPI's Custom-LLM
            Authorization header to send exactly that token.
          </div>
        ) : (
          <div
            data-testid="new-key-secret"
            style={{
              padding: 12,
              background: 'var(--bg-alt2)',
              border: '1px solid var(--accent)',
              borderRadius: 4,
              fontFamily: 'ui-monospace, monospace',
              fontSize: 13,
              color: 'var(--text)',
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginBottom: 20,
              wordBreak: 'break-all',
            }}
          >
            <span style={{ flex: 1 }}>{result.secret}</span>
            <CopyButton text={result.secret} label="Copy secret" testId="copy-secret-button" />
          </div>
        )}

        {isMcp ? (
          <>
            <div style={{ fontSize: 13, color: 'var(--text)', marginBottom: 8, fontWeight: 500 }}>
              Claude Desktop configuration
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-soft)', marginBottom: 8 }}>
              Paste this into <code>claude_desktop_config.json</code>:
            </div>
            <CodeBlock testId="claude-desktop-snippet">{claudeDesktopSnippet}</CodeBlock>
          </>
        ) : isOai ? (
          <>
            <div style={{ fontSize: 13, color: 'var(--text)', marginBottom: 8, fontWeight: 500 }}>
              VAPI custom-LLM configuration
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-soft)', marginBottom: 8 }}>
              Point VAPI's custom-LLM model block at the OpenAI-compatible endpoint:
            </div>
            <CodeBlock testId="vapi-snippet">{vapiSnippet}</CodeBlock>
            <div style={{ fontSize: 11, color: 'var(--text-soft)', margin: '12px 0 8px' }}>
              Or test it directly with curl (streams Server-Sent Events):
            </div>
            <CodeBlock testId="oai-curl-snippet">{oaiCurlSnippet}</CodeBlock>
          </>
        ) : (
          <>
            <div style={{ fontSize: 13, color: 'var(--text)', marginBottom: 8, fontWeight: 500 }}>
              Sample curl call
            </div>
            <div style={{ fontSize: 11, color: 'var(--text-soft)', marginBottom: 8 }}>
              Hits the agent-server REST surface directly (no service-auth header needed for the direct_api audience):
            </div>
            <CodeBlock testId="curl-snippet">{curlSnippet}</CodeBlock>
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 20 }}>
          <button
            data-testid="new-key-close"
            onClick={onClose}
            style={{
              padding: '8px 16px',
              fontSize: 13,
              border: 0,
              borderRadius: 4,
              background: 'var(--accent)',
              color: '#fff',
              cursor: 'pointer',
              fontWeight: 500,
            }}
          >
            I've copied it
          </button>
        </div>
      </div>
    </div>
  );
}
